// @flow
import React, { useEffect } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { ActivityIndicator, EmphasizedSubText, Text } from 'trpc-react-native-ui';
import { NavigationRoute, NavigationScreenProp } from '@react-navigation/compat';
import { FullButton, ResultPage, SecondaryFullButton } from '../../components/common';
import theme from '../../theme-config';
import type { PaymentAuthenticationResultType } from '../../modules/Payment/reducer';
import I18n from '../../lib/i18n';

const styles = StyleSheet.create({
  buttonsContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    justifyContent: 'space-around',
  },
  contentContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 4,
  },
  failText: {
    color: theme.primaryColor,
  },
  goToDashboardButton: {
    marginHorizontal: 20,
    marginTop: 20,
  },
  image: {
    height: 200,
    marginBottom: 25,
    marginLeft: 10,
    marginTop: 30,
    width: 150,
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 5,
  },
  secondaryButton: {
    marginTop: 40,
  },
  successText: {
    textAlign: 'center',
  },
  text: {
    fontSize: 18,
    lineHeight: 22,
  },
  textContainer: {
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  viewPaymentButton: {
    marginHorizontal: 20,
  },
});

export const PaymentAuthorizationResult = ({
  checkPaymentAuthorizationResult,
  goToDashboard,
  route,
  paymentAuthenticationResult,
  viewPayment,
}: Props) => {
  const stripeIntentId = route.params?.stripeIntentId;
  const stripeRedirectUrl = route.params?.stripeRedirectUrl;
  const notificationOuid = route.params?.notificationOuid;
  const processName = route.params?.processName;

  useEffect(() => {
    if (stripeIntentId) {
      checkPaymentAuthorizationResult(stripeIntentId, stripeRedirectUrl, notificationOuid);
    }
  }, []);

  const getTranslationPathByPrefix = (prefix: string) =>
    processName === 'renewalProcess'
      ? I18n.t(`scenes.PaymentAuthorization.result.${prefix}Renewal`)
      : I18n.t(`scenes.PaymentAuthorization.result.${prefix}`);

  const onViewPaymentButtonClick = () => {
    viewPayment(stripeIntentId, stripeRedirectUrl, notificationOuid);
  };

  const PendingAuthentContent = (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.textContainer}>
          <EmphasizedSubText
            style={[styles.text, styles.failText]}
            text={getTranslationPathByPrefix('pendingAuthent')}
          />
        </View>
      </View>
      <View style={styles.buttonsContainer}>
        <FullButton
          style={styles.viewPaymentButton}
          onPress={onViewPaymentButtonClick}
          text={I18n.t('scenes.PaymentAuthorization.result.viewPayment')}
        />
        <SecondaryFullButton
          style={styles.goToDashboardButton}
          onPress={goToDashboard}
          text={I18n.t('scenes.PaymentAuthorization.result.returnToDashboard')}
        />
      </View>
    </View>
  );

  const FailContent = () => (
    <View style={styles.textContainer}>
      <EmphasizedSubText style={[styles.text, styles.failText]} text={getTranslationPathByPrefix('fail')} />
    </View>
  );

  const SuccessContent = (
    <>
      <Image style={styles.image} source={theme.successPhoneImage} resizeMode="contain" />
      <View style={styles.textContainer}>
        <EmphasizedSubText
          style={[styles.text, styles.successText]}
          text={I18n.t('scenes.PaymentAuthorization.result.success')}
        />
      </View>
    </>
  );

  if (paymentAuthenticationResult === 'PENDING') {
    return (
      <View style={styles.loading}>
        <ActivityIndicator />
        <Text style={styles.loadingText}>{I18n.t('SignUp.Payment.loaderText1')}</Text>
        <Text style={styles.loadingText}>{I18n.t('SignUp.Payment.loaderText2')}</Text>
      </View>
    );
  }

  if (paymentAuthenticationResult === 'PENDING_AUTHENT') {
    return PendingAuthentContent;
  }

  return (
    <ResultPage
      handleButtonPress={goToDashboard}
      buttonText={I18n.t('scenes.PaymentAuthorization.result.returnToDashboard')}
      resultContent={paymentAuthenticationResult === 'SUCCESS' ? SuccessContent : FailContent()}
    />
  );
};

export type NavigationRouteProps = {
  notificationOuid: string,
  processName: string,
  stripeIntentId: string,
  stripeRedirectUrl: string,
};

export interface Props {
  checkPaymentAuthorizationResult: (
    stripeIntentId: string,
    stripeRedirectUrl: string,
    notificationOuid: string
  ) => void;
  buttonText: string;
  failText: string;
  goToDashboard: () => void;
  route: NavigationScreenProp<NavigationRoute, NavigationRouteProps>;
  paymentAuthenticationResult: PaymentAuthenticationResultType;
  setIsLoading: (isLoading: boolean) => void;
  successText: string;
  viewPayment: (stripeIntentId: string, stripeRedirectUrl: string, notificationOuid: string) => void;
}
